/**
 * Google Drive Extension - continuity teach harness
 * v6: One AddressBar row on Drive (no ProtoTitleRow duplicate); ExtensionListRow;
 * resizable SecondaryPanel. v5 status bar + folder-view + auth mock.
 * Aligned to /?test=maater-ui + maater-extension-ui.
 *
 * Access: http://app.maater.localhost/?test=google-drive-extension
 *
 * SoT: /?test=maater-ui for icons/menus/theme
 * Guide: docs/design/maater-list-surface-continuity.md
 * Bet: docs/product/bets/2026-08-03-google-drive-browse-folder-view.md
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { fetchWithAuth } from '@/lib/api'
import {
  ArrowUp,
  Check,
  Clock,
  Download,
  ExternalLink,
  FileText,
  Folder,
  Image as ImageIcon,
  Import,
  Link2,
  Loader2,
  MessageSquare,
  MoreHorizontal,
  RefreshCw,
  X,
} from 'lucide-react'
import { GoogleDriveIcon } from '@/components/icons/google-drive-icon'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { useGoogleDriveAuth } from '@/hooks/use-google-drive-auth'
import { openOAuthPopup, ApiTestPanel } from '@/components/test-google-drive-extension.api'
import { testIds } from '@/lib/test-ids'
import {
  MOCK_MY_DRIVE,
  MOCK_RECENT,
  MOCK_SHARED_DRIVES,
  mockMimeForKind,
  type MockDriveFileKind,
  type MockDriveFileMeta,
} from '@/components/integrations/google-workspace/mock-drive-tree'
import { DriveImportPickerTrigger } from '@/components/integrations/google-workspace/drive-import-picker'
import { HarnessDrivePreviewStub } from '@/components/integrations/google-workspace/harness-drive-preview-stub'
import { PreviewPanelToggle } from '@/components/integrations/_shared/preview-panel-toggle'
import {
  EntityContextMenu,
  type EntityContextMenuEntry,
} from '@/components/features/entity-context-menu'
import { Panel, PanelGroup } from 'react-resizable-panels'
import { SidebarTreeRow } from '@/components/layout/sidebar/SidebarTreeRow'
import { AddressBar, type BreadcrumbSegment } from '@/components/ui/address-bar'
import { NavigationButtons } from '@/components/ui/navigation-buttons'
import { ExtensionListRow } from '@/components/integrations/_shared/extension-list-row'
import { SecondaryPreviewResizeHandle } from '@/components/integrations/_shared/secondary-preview-resize-handle'
import { SecondaryPanel } from '@/components/layout/secondary-panel'
import { PreviewPaneHeader } from '@/components/features/preview-pane-header'
import { PANEL_SIZES } from '@/lib/panel-sizes'
import {
  maaterProtoAppSkinStyle,
  MaaterProtoSegmentTrack,
} from '@/components/prototype/maater-proto-shell'
import {
  ProtoAppHeader,
  ProtoIconRail,
  ProtoModePanel,
  ProtoTabRow,
  ProtoTitleRow,
} from '@/components/prototype/maater-proto-chrome'
import { useTheme } from '@/providers/theme-provider'
import {
  OpenDestinationIcons,
  resolveOpenExternalIcon,
} from '@/lib/open-destination-icons'
import {
  driveEffectiveMime,
  isDriveFileImportable,
} from '@/lib/drive-importability'
import { DRIVE_ACCESS_COPY } from '@/lib/google-drive-access'
import { ListSurfaceStatusBar } from '@/components/ui/list-surface-status-bar'
import { LIST_GESTURE_TIPS_DRIVE_WITH_PREVIEW } from '@/lib/list-gesture-tips'

// --- Types ----------------------------------------------------------------

type TopView = 'prototype' | 'api'
type Surface = 'drive' | 'activity' | 'linked' | 'viewer'
type ProtoDriveCorpus = 'myDrive' | 'sharedDrives' | 'recent'
type SyncFrame = 'idle' | 'syncing' | 'synced' | 'conflict'
/** Harness-only auth empty teaching (does not require live OAuth). */
type AuthMockFrame = 'live' | 'disconnected' | 'blocked'

type CtxKind = 'file' | 'activity' | 'linked'

type DriveNode = {
  id: string
  name: string
  type: MockDriveFileKind
  modified: string
  children?: DriveNode[]
  shortcutTarget?: { id: string; mimeType: string; name?: string }
}

type LinkedDoc = {
  id: string
  fileId: string
  title: string
  /** User-facing path only - never ws/hex */
  displayPath: string
  keepSynced: boolean
  commentCount: number
}

type ActivityRow = {
  id: string
  fileId: string
  fileName: string
  author: string
  authorInitials: string
  content: string
  timestamp: string
  kind: 'comment' | 'sync' | 'mention'
  /** When set, Activity menu can Open linked Maater doc */
  linkedDocId?: string
}

type CtxMenu = {
  x: number
  y: number
  kind: CtxKind
  id: string
  fileId: string
  linkedDocId?: string
  keepSynced?: boolean
}

// --- Mock data ------------------------------------------------------------

const MOCK_DRIVE = MOCK_MY_DRIVE as DriveNode[]

const MOCK_LINKED: LinkedDoc[] = [
  {
    id: 'ld1',
    fileId: 'd1',
    title: 'The Architects (Book Draft)',
    displayPath: 'Personal / google-drive-imports / the-architects.md',
    keepSynced: true,
    commentCount: 3,
  },
  {
    id: 'ld2',
    fileId: 'd4',
    title: 'Editorial Feedback - May',
    displayPath: 'Personal / google-drive-imports / editorial-feedback-may.md',
    keepSynced: false,
    commentCount: 1,
  },
]

const MOCK_ACTIVITY: ActivityRow[] = [
  {
    id: 'a1',
    fileId: 'd1',
    fileName: 'The Architects (Book Draft)',
    author: 'Alice Chen',
    authorInitials: 'AC',
    content: 'Love this opening. The Neuromancer reference is subtle - maybe too subtle?',
    timestamp: '2 hours ago',
    kind: 'comment',
    linkedDocId: 'ld1',
  },
  {
    id: 'a2',
    fileId: 'd1',
    fileName: 'The Architects (Book Draft)',
    author: 'Maater',
    authorInitials: 'M',
    content: 'Synced - pulled latest from Drive (linebreak normalize).',
    timestamp: '3 hours ago',
    kind: 'sync',
    linkedDocId: 'ld1',
  },
  {
    id: 'a3',
    fileId: 'd4',
    fileName: 'Editorial Feedback - May',
    author: 'Marcus Webb',
    authorInitials: 'MW',
    content: 'Action assigned to you: Update the campaign timeline.',
    timestamp: '1 day ago',
    kind: 'mention',
    linkedDocId: 'ld2',
  },
  {
    id: 'a4',
    fileId: 'd2',
    fileName: 'Chapter Notes',
    author: 'Alice Chen',
    authorInitials: 'AC',
    content: 'Added notes for scene transitions in Part II.',
    timestamp: '3 days ago',
    kind: 'comment',
  },
]

const SURFACES: { id: Surface; label: string; icon: ReactNode }[] = [
  { id: 'drive', label: 'Drive', icon: <GoogleDriveIcon className="h-3.5 w-3.5" /> },
  { id: 'activity', label: 'Activity', icon: <MessageSquare className="h-3.5 w-3.5" /> },
  { id: 'linked', label: 'Linked', icon: <Link2 className="h-3.5 w-3.5" /> },
]

// --- Helpers --------------------------------------------------------------

function findNode(nodes: DriveNode[], id: string): DriveNode | null {
  for (const n of nodes) {
    if (n.id === id) return n
    if (n.children) {
      const hit = findNode(n.children, id)
      if (hit) return hit
    }
  }
  return null
}

function folderChildren(folderId: string | null): DriveNode[] {
  if (!folderId) return MOCK_DRIVE
  const folder = findNode(MOCK_DRIVE, folderId)
  return folder?.children ?? []
}

function findParentFolderId(nodes: DriveNode[], childId: string, parentId: string | null = null): string | null | undefined {
  for (const n of nodes) {
    if (n.id === childId) return parentId
    if (n.children) {
      const hit = findParentFolderId(n.children, childId, n.type === 'folder' ? n.id : parentId)
      if (hit !== undefined) return hit
    }
  }
  return undefined
}

function folderBreadcrumbSegments(
  folderId: string | null,
  onSelect: (id: string | null) => void,
): BreadcrumbSegment[] {
  const segments: BreadcrumbSegment[] = [
    {
      label: 'My Drive',
      path: folderId === null ? null : 'root',
      onClick: folderId === null ? undefined : () => onSelect(null),
    },
  ]
  if (!folderId) return segments

  const trail: DriveNode[] = []
  const walk = (nodes: DriveNode[], target: string, acc: DriveNode[]): boolean => {
    for (const n of nodes) {
      if (n.type !== 'folder') continue
      const next = [...acc, n]
      if (n.id === target) {
        trail.push(...next)
        return true
      }
      if (n.children && walk(n.children, target, next)) return true
    }
    return false
  }
  walk(MOCK_DRIVE, folderId, [])
  for (let i = 0; i < trail.length; i++) {
    const node = trail[i]
    const isLast = i === trail.length - 1
    segments.push({
      label: node.name,
      path: isLast ? null : node.id,
      onClick: isLast ? undefined : () => onSelect(node.id),
    })
  }
  return segments
}

function fileMeta(fileId: string): MockDriveFileMeta | null {
  const n = findNode(MOCK_DRIVE, fileId)
  if (n && n.type !== 'folder') {
    return {
      fileId: n.id,
      type: n.type,
      mimeType: mockMimeForKind(n.type),
      shortcutTarget: n.shortcutTarget ?? null,
    }
  }
  const recent = MOCK_RECENT.find((r) => r.id === fileId)
  if (recent) {
    return {
      fileId: recent.id,
      type: recent.type,
      mimeType: mockMimeForKind(recent.type),
      shortcutTarget: recent.shortcutTarget ?? null,
    }
  }
  return null
}

function FileIcon({ type }: { type: DriveNode['type'] }) {
  if (type === 'folder') return <Folder className="h-4 w-4 text-amber-500/80" />
  if (type === 'doc') return <FileText className="h-4 w-4 text-blue-500/80" />
  if (type === 'image') return <ImageIcon className="h-4 w-4 text-violet-500/80" />
  if (type === 'shortcut') return <Link2 className="h-4 w-4 text-purple-500/80" />
  if (type === 'pdf') return <FileText className="h-4 w-4 text-red-500/70" />
  return <FileText className="h-4 w-4 text-emerald-500/80" />
}

function fileIsImportable(meta: MockDriveFileMeta | null): boolean {
  if (!meta) return false
  return isDriveFileImportable(
    driveEffectiveMime(meta.mimeType, meta.shortcutTarget?.mimeType),
  )
}

function Toast({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return (
    <div className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-lg bg-foreground px-4 py-2.5 text-sm font-medium text-background shadow-xl">
      <Check className="h-4 w-4 text-green-400" />
      {message}
      <button type="button" onClick={onDismiss} className="ml-1 opacity-60 hover:opacity-100">
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

// --- Import picker (sheet) ------------------------------------------------

function ImportPicker({
  onClose,
  onImport,
}: {
  onClose: () => void
  onImport: (count: number) => void
}) {
  const [driveUrl, setDriveUrl] = useState('')
  const [importing, setImporting] = useState(false)
  const [progressLabel, setProgressLabel] = useState('')

  const startImport = async (label: string) => {
    setImporting(true)
    setProgressLabel(label)
    await new Promise((r) => setTimeout(r, 900))
    setProgressLabel('Created in Maater')
    setTimeout(() => onImport(1), 400)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <div
        className="flex w-[440px] flex-col rounded-xl border shadow-2xl"
        style={{ borderColor: 'var(--line)', background: 'var(--paper-raised)' }}
      >
        <div
          className="flex items-center justify-between border-b px-5 py-4"
          style={{ borderColor: 'var(--line)' }}
        >
          <div className="space-y-0.5">
            <h2 className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>
              Import from Google Drive
            </h2>
            <p className="text-xs" style={{ color: 'var(--ink-soft)' }}>
              Paste a link, or choose files. Tree browse + row Import is for files you already see.
            </p>
          </div>
          <button type="button" onClick={onClose} style={{ color: 'var(--ink-soft)' }}>
            <X className="h-4 w-4" />
          </button>
        </div>
        {!importing ? (
          <div className="space-y-4 px-5 py-4">
            <div className="space-y-2">
              <label className="text-xs font-medium" style={{ color: 'var(--ink)' }}>
                Paste Drive or Docs URL
              </label>
              <div className="flex gap-2">
                <input
                  value={driveUrl}
                  onChange={(e) => setDriveUrl(e.target.value)}
                  placeholder="https://docs.google.com/document/d/..."
                  className="h-9 flex-1 rounded-md border px-2 text-xs"
                  style={{ borderColor: 'var(--line)', background: 'var(--paper)', color: 'var(--ink)' }}
                />
                <Button
                  size="sm"
                  className="h-9 text-xs"
                  disabled={!driveUrl.trim()}
                  onClick={() => void startImport(driveUrl.trim())}
                >
                  Import
                </Button>
              </div>
            </div>
            <div className="relative flex items-center gap-3 py-1">
              <div className="h-px flex-1" style={{ background: 'var(--line)' }} />
              <span className="text-[10px] uppercase" style={{ color: 'var(--ink-faint)' }}>
                or
              </span>
              <div className="h-px flex-1" style={{ background: 'var(--line)' }} />
            </div>
            <Button
              variant="secondary"
              className="h-9 w-full text-xs"
              onClick={() => void startImport('Alice book (mock picker)')}
            >
              Choose files from Drive…
            </Button>
            <Button variant="ghost" size="sm" className="w-full text-xs" onClick={onClose}>
              Cancel
            </Button>
          </div>
        ) : (
          <div className="space-y-3 p-5 text-xs">
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              <span>Importing as markdown...</span>
            </div>
            <p style={{ color: 'var(--ink-soft)' }}>{progressLabel}</p>
          </div>
        )}
      </div>
    </div>
  )
}

// --- Mode panel -----------------------------------------------------------

const TREE_PAD = [20, 32, 44, 56, 68]

function FolderTreeNav({
  nodes,
  currentFolderId,
  expanded,
  onToggle,
  onSelectFolder,
  depth = 0,
}: {
  nodes: DriveNode[]
  currentFolderId: string | null
  expanded: Set<string>
  onToggle: (id: string) => void
  onSelectFolder: (id: string | null) => void
  depth?: number
}) {
  const pad = TREE_PAD[Math.min(depth, TREE_PAD.length - 1)]
  return (
    <div className="space-y-0.5" role={depth === 0 ? 'tree' : 'group'}>
      {depth === 0 && (
        <SidebarTreeRow
          padLeft={pad}
          active={currentFolderId === null}
          icon={<Folder className="h-3.5 w-3.5 text-yellow-500/80" />}
          label="My Drive"
          aria-level={1}
          className="cursor-pointer"
          chevron={{
            expanded: expanded.has('root'),
            onClick: () => onToggle('root'),
          }}
          onClick={() => {
            onSelectFolder(null)
            if (!expanded.has('root')) onToggle('root')
          }}
        />
      )}
      {(depth === 0 ? (expanded.has('root') ? nodes : []) : nodes)
        .filter((n) => n.type === 'folder')
        .map((folder) => {
          const isOpen = expanded.has(folder.id)
          const active = currentFolderId === folder.id
          const childPad = TREE_PAD[Math.min(depth + 1, TREE_PAD.length - 1)]
          const hasFolderKids = Boolean(folder.children?.some((c) => c.type === 'folder'))
          return (
            <div key={folder.id}>
              <SidebarTreeRow
                padLeft={childPad}
                active={active}
                icon={<Folder className="h-3.5 w-3.5 text-yellow-500/80" />}
                label={folder.name}
                aria-level={depth + 2}
                aria-expanded={hasFolderKids ? isOpen : undefined}
                className="cursor-pointer"
                chevron={{
                  expanded: isOpen,
                  onClick: () => onToggle(folder.id),
                }}
                onClick={() => {
                  onSelectFolder(folder.id)
                  if (hasFolderKids && !isOpen) onToggle(folder.id)
                }}
              />
              {isOpen && folder.children ? (
                <FolderTreeNav
                  nodes={folder.children}
                  currentFolderId={currentFolderId}
                  expanded={expanded}
                  onToggle={onToggle}
                  onSelectFolder={onSelectFolder}
                  depth={depth + 1}
                />
              ) : null}
            </div>
          )
        })}
    </div>
  )
}

function DriveModePanel({
  surface,
  onSurface,
  currentFolderId,
  onSelectFolder,
  connected,
  authLoading,
  onConnect,
  onDisconnect,
}: {
  surface: Surface
  onSurface: (s: Surface) => void
  currentFolderId: string | null
  onSelectFolder: (id: string | null) => void
  connected: boolean
  authLoading: boolean
  onConnect: () => void
  onDisconnect: () => void
}) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(['f1']))

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <ProtoModePanel
      title="Google Drive"
      testId={testIds.googleDriveTest.modePanel}
      footer={
        <div className="flex items-center gap-2 px-1">
          {authLoading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
          ) : connected ? (
            <>
              <Badge className="border-green-500/20 bg-green-500/10 text-[10px] text-green-600">
                Connected
              </Badge>
              <button
                type="button"
                onClick={onDisconnect}
                className="text-[10px] text-muted-foreground hover:text-destructive"
                data-testid={testIds.googleDriveTest.disconnectButton}
              >
                Disconnect
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onConnect}
              className="rounded bg-primary px-2 py-0.5 text-[10px] font-medium text-primary-foreground"
              data-testid={testIds.googleDriveTest.connectButton}
            >
              Connect
            </button>
          )}
        </div>
      }
    >
      <nav className="mb-3 space-y-0.5" data-testid={testIds.googleDriveTest.surfaceNav}>
        {SURFACES.map((s) => {
          const active = surface === s.id || (surface === 'viewer' && s.id === 'drive')
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => onSurface(s.id)}
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors"
              style={
                active
                  ? {
                      background: 'var(--rust-pale)',
                      color: 'var(--ink)',
                      boxShadow: 'inset 2px 0 0 var(--rust)',
                    }
                  : { color: 'var(--ink-soft)' }
              }
            >
              <span style={{ color: active ? 'var(--rust)' : 'var(--ink-faint)' }}>{s.icon}</span>
              <span className="font-medium">{s.label}</span>
            </button>
          )
        })}
      </nav>
      {/* Thin folder tree stays across nouns - only main list swaps */}
      <div className="space-y-1">
        <p
          className="px-2 text-[10px] font-semibold uppercase tracking-wider"
          style={{ color: 'var(--ink-faint)' }}
        >
          Folders
        </p>
        <FolderTreeNav
          nodes={MOCK_DRIVE}
          currentFolderId={currentFolderId}
          expanded={expanded}
          onToggle={toggle}
          onSelectFolder={onSelectFolder}
        />
      </div>
    </ProtoModePanel>
  )
}

// --- Sync strip -----------------------------------------------------------

function SyncActivityStrip({
  frame,
  keepSynced,
  onSync,
  onToggleKeep,
  onDemoFrame,
}: {
  frame: SyncFrame
  keepSynced: boolean
  onSync: () => void
  onToggleKeep: () => void
  onDemoFrame: (f: SyncFrame) => void
}) {
  const label =
    frame === 'syncing'
      ? 'Syncing with Google Drive...'
      : frame === 'synced'
        ? 'Synced'
        : frame === 'conflict'
          ? 'Conflict - both sides changed'
          : keepSynced
            ? 'Keep synced on'
            : 'Ready to sync'

  return (
    <div
      className="flex h-9 shrink-0 items-center gap-2 border-b px-3"
      style={{ borderColor: 'var(--line)', background: 'var(--paper-deep)' }}
      data-testid={testIds.googleDriveTest.syncStrip}
    >
      {frame === 'syncing' ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: 'var(--rust)' }} />
      ) : frame === 'conflict' ? (
        <AlertDot />
      ) : (
        <RefreshCw className="h-3.5 w-3.5" style={{ color: 'var(--ink-faint)' }} />
      )}
      <span className="flex-1 text-xs font-medium" style={{ color: 'var(--ink)' }}>
        {label}
      </span>
      <Button
        size="sm"
        variant="outline"
        className="h-7 gap-1 text-xs"
        onClick={onSync}
        disabled={frame === 'syncing'}
        data-testid={testIds.googleDriveTest.syncButton}
      >
        <RefreshCw className="h-3 w-3" />
        Sync
      </Button>
      <button
        type="button"
        onClick={onToggleKeep}
        className={cn(
          'rounded-md border px-2 py-1 text-[10px] font-medium',
          keepSynced && 'border-primary/40 bg-primary/10',
        )}
        style={{ borderColor: keepSynced ? undefined : 'var(--line)', color: 'var(--ink)' }}
        data-testid={testIds.googleDriveTest.keepSyncedToggle}
      >
        Keep synced {keepSynced ? 'on' : 'off'}
      </button>
      <div className="hidden items-center gap-1 sm:flex" title="Harness: demo sync frames">
        <MaaterProtoSegmentTrack
          value={frame === 'idle' ? 'synced' : frame}
          onChange={(id) => onDemoFrame(id as SyncFrame)}
          options={[
            { id: 'syncing', label: '···' },
            { id: 'synced', label: 'OK' },
            { id: 'conflict', label: '!' },
          ]}
        />
      </div>
    </div>
  )
}

function AlertDot() {
  return <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />
}

// --- Main -----------------------------------------------------------------

export function GoogleDriveExtensionTest() {
  const { theme, setTheme } = useTheme()
  const [topView, setTopView] = useState<TopView>('prototype')
  const [surface, setSurface] = useState<Surface>('drive')
  const [history, setHistory] = useState<{ stack: Surface[]; index: number }>({
    stack: ['drive'],
    index: 0,
  })
  const [currentFolderId, setCurrentFolderId] = useState<string | null>('f1')
  const [selectedFileId, setSelectedFileId] = useState<string | null>('d1')
  const [previewOpen, setPreviewOpen] = useState(true)
  const [viewerFileId, setViewerFileId] = useState<string | null>(null)
  const [showPicker, setShowPicker] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [ctxMenu, setCtxMenu] = useState<CtxMenu | null>(null)
  const [syncFrame, setSyncFrame] = useState<SyncFrame>('synced')
  const [keepSynced, setKeepSynced] = useState(true)
  const [selectedActivityId, setSelectedActivityId] = useState<string | null>('a1')
  const [selectedLinkedId, setSelectedLinkedId] = useState<string | null>('ld1')
  const [authMock, setAuthMock] = useState<AuthMockFrame>('live')
  const [folderHistory, setFolderHistory] = useState<{
    stack: Array<string | null>
    index: number
  }>({ stack: ['f1'], index: 0 })

  const { authStatus, isLoading: authLoading, refetch } = useGoogleDriveAuth()
  const liveConnected = authStatus?.connected ?? false
  const connected = authMock === 'live' ? liveConnected : false
  const showBlockedEmpty = authMock === 'blocked'
  const showDisconnectedEmpty = authMock === 'disconnected'

  const showToast = (msg: string) => {
    setToast(`Harness: ${msg}`)
    setTimeout(() => setToast(null), 2800)
  }

  const pushSurface = (s: Surface) => {
    setHistory((prev) => {
      const trimmed = prev.stack.slice(0, prev.index + 1)
      if (trimmed[trimmed.length - 1] === s) return prev
      const stack = [...trimmed, s]
      return { stack, index: stack.length - 1 }
    })
    setSurface(s)
  }

  const goBack = () => {
    if (history.index <= 0) return
    const index = history.index - 1
    setHistory({ ...history, index })
    setSurface(history.stack[index] ?? 'drive')
  }

  const goForward = () => {
    if (history.index >= history.stack.length - 1) return
    const index = history.index + 1
    setHistory({ ...history, index })
    setSurface(history.stack[index] ?? 'drive')
  }

  const handleSurface = (s: Surface) => {
    if (s !== 'viewer') setViewerFileId(null)
    pushSurface(s)
  }

  const selectFile = (fileId: string) => {
    setSelectedFileId(fileId)
  }

  const openViewer = (fileId: string) => {
    setSelectedFileId(fileId)
    setViewerFileId(fileId)
    pushSurface('viewer')
  }

  const runSync = () => {
    setSyncFrame('syncing')
    window.setTimeout(() => setSyncFrame('synced'), 1600)
  }

  useEffect(() => {
    if (!ctxMenu) return
    const close = () => setCtxMenu(null)
    window.addEventListener('click', close)
    return () => window.removeEventListener('click', close)
  }, [ctxMenu])

  const folderLabel =
    currentFolderId === null
      ? 'My Drive'
      : (findNode(MOCK_DRIVE, currentFolderId)?.name ?? 'My Drive')

  const listFiles = useMemo(() => folderChildren(currentFolderId), [currentFolderId])

  const selectedMeta = selectedFileId ? fileMeta(selectedFileId) : null
  const selectedDriveNode = selectedFileId
    ? (listFiles.find((f) => f.id === selectedFileId)
        ?? findNode(MOCK_DRIVE, selectedFileId)
        ?? null)
    : null
  const viewerMeta = viewerFileId ? fileMeta(viewerFileId) : null
  const viewerNode = viewerFileId ? findNode(MOCK_DRIVE, viewerFileId) : null
  const selectedImportable = fileIsImportable(selectedMeta)
  const viewerImportable = fileIsImportable(viewerMeta)

  const navigateFolder = useCallback((id: string | null) => {
    setCurrentFolderId(id)
    setFolderHistory((prev) => {
      const trimmed = prev.stack.slice(0, prev.index + 1)
      if (trimmed[trimmed.length - 1] === id) return prev
      const stack = [...trimmed, id]
      return { stack, index: stack.length - 1 }
    })
    if (surface !== 'drive') handleSurface('drive')
    else setSurface('drive')
  }, [surface])

  const folderGoBack = () => {
    if (folderHistory.index <= 0) return
    const index = folderHistory.index - 1
    setFolderHistory({ ...folderHistory, index })
    setCurrentFolderId(folderHistory.stack[index] ?? null)
  }

  const folderGoForward = () => {
    if (folderHistory.index >= folderHistory.stack.length - 1) return
    const index = folderHistory.index + 1
    setFolderHistory({ ...folderHistory, index })
    setCurrentFolderId(folderHistory.stack[index] ?? null)
  }

  const folderGoUp = () => {
    if (currentFolderId === null) return
    const parent = findParentFolderId(MOCK_DRIVE, currentFolderId)
    navigateFolder(parent === undefined ? null : parent)
  }

  const breadcrumbSegments = useMemo(
    () => folderBreadcrumbSegments(currentFolderId, navigateFolder),
    [currentFolderId, navigateFolder],
  )

  const ctxMenuEntries = useMemo((): EntityContextMenuEntry[] => {
    if (!ctxMenu) return []

    if (ctxMenu.kind === 'activity') {
      const entries: EntityContextMenuEntry[] = [
        {
          label: 'Preview',
          icon: OpenDestinationIcons.preview,
          onSelect: () => {
            setPreviewOpen(true)
            selectFile(ctxMenu.fileId)
          },
        },
        {
          label: 'Open in Google Drive',
          icon: ExternalLink,
          onSelect: () => showToast('Open in Drive (mock)'),
        },
      ]
      if (ctxMenu.linkedDocId) {
        entries.push({
          label: 'Open linked Maater doc',
          icon: OpenDestinationIcons.open,
          onSelect: () => {
            setSelectedLinkedId(ctxMenu.linkedDocId!)
            handleSurface('linked')
            showToast('Would open linked Maater doc')
          },
        })
      }
      return entries
    }

    if (ctxMenu.kind === 'linked') {
      const entries: EntityContextMenuEntry[] = [
        {
          label: 'Open in Maater',
          icon: OpenDestinationIcons.open,
          onSelect: () => openViewer(ctxMenu.fileId),
        },
        {
          label: 'Open in Google Drive',
          icon: ExternalLink,
          onSelect: () => showToast('Open in Drive (mock)'),
        },
      ]
      if (ctxMenu.keepSynced) {
        entries.push({
          label: 'Sync with Drive',
          icon: RefreshCw,
          onSelect: () => runSync(),
        })
      }
      return entries
    }

    const meta = fileMeta(ctxMenu.fileId)
    const importable = fileIsImportable(meta)
    const listed = listFiles.find((f) => f.id === ctxMenu.fileId)
    const isFolder = listed?.type === 'folder'
    const entries: EntityContextMenuEntry[] = [
      {
        label: 'Preview',
        icon: OpenDestinationIcons.preview,
        onSelect: () => {
          setPreviewOpen(true)
          selectFile(ctxMenu.fileId)
        },
      },
      {
        label: 'Open',
        icon: OpenDestinationIcons.open,
        onSelect: () => {
          if (isFolder) navigateFolder(ctxMenu.fileId)
          else openViewer(ctxMenu.fileId)
        },
      },
    ]
    if (importable) {
      entries.push({
        label: 'Import to Maater',
        icon: Download,
        onSelect: () => showToast('Import queued (mock)'),
      })
    }
    entries.push({
      label: 'Open in Drive',
      icon: resolveOpenExternalIcon(GoogleDriveIcon),
      onSelect: () => showToast('Open in Drive (mock)'),
    })
    return entries
  }, [ctxMenu, listFiles, navigateFolder])

  const tabs = useMemo(() => {
    const base = [{ id: 'drive', label: 'Drive' }]
    if (surface === 'activity') return [...base, { id: 'activity', label: 'Activity' }]
    if (surface === 'linked') return [...base, { id: 'linked', label: 'Linked' }]
    if (surface === 'viewer' && viewerNode) {
      return [...base, { id: 'viewer', label: viewerNode.name.replace(/\.gdoc$/, '') }]
    }
    return base
  }, [surface, viewerNode])

  const activeTabId = surface === 'drive' ? 'drive' : surface

  const titleRow = (() => {
    if (surface === 'viewer' && viewerNode) {
      return {
        crumbs: [
          { id: 'drive', label: 'Drive' },
          { id: 'folder', label: folderLabel },
          { id: 'file', label: viewerNode.name.replace(/\.gdoc$/, '') },
        ],
        title: viewerNode.name.replace(/\.gdoc$/, ''),
        subtitle: 'Linked Maater doc - Sync and Keep synced are primary chrome',
      }
    }
    if (surface === 'activity') {
      return {
        crumbs: [
          { id: 'drive', label: 'Drive' },
          { id: 'activity', label: 'Activity' },
        ],
        title: 'Activity',
        subtitle: 'Comments and sync events across linked Drive files',
      }
    }
    if (surface === 'linked') {
      return {
        crumbs: [
          { id: 'drive', label: 'Drive' },
          { id: 'linked', label: 'Linked' },
        ],
        title: 'Linked documents',
        subtitle: 'Imported docs with gdoc_id - paths are user-facing only',
      }
    }
    return {
      crumbs: [
        { id: 'drive', label: 'Drive' },
        { id: 'folder', label: folderLabel },
      ],
      title: folderLabel,
      subtitle:
        'Browse Drive here. Import from the row menu, or Import for URL / multi-select. Search box = Google Drive keyword search.',
    }
  })()

  const handleConnect = useCallback(async () => {
    try {
      await openOAuthPopup()
    } catch (err) {
      console.error(err)
      showToast('Connect failed - check API')
    }
  }, [])

  const disconnectProper = useCallback(async () => {
    const res = await fetchWithAuth('/api/google-drive/auth/disconnect', { method: 'DELETE' })
    if (res.ok) {
      await refetch()
      showToast('Google Drive disconnected')
    }
  }, [refetch])

  if (topView === 'api') {
    return (
      <div className="flex h-screen flex-col bg-background">
        <div className="flex h-10 items-center gap-2 border-b border-border px-3">
          <MaaterProtoSegmentTrack
            value={topView}
            onChange={(id) => setTopView(id as TopView)}
            options={[
              { id: 'prototype', label: 'Prototype' },
              { id: 'api', label: 'API Test' },
            ]}
          />
          <a href="/?test=index" className="ml-auto text-xs text-muted-foreground hover:underline">
            Prototypes
          </a>
        </div>
        <ApiTestPanel />
      </div>
    )
  }

  return (
    <div
      className="flex h-screen min-h-screen flex-col bg-background text-foreground antialiased"
      style={maaterProtoAppSkinStyle()}
      data-maater-gallery-skin="app"
      data-testid={testIds.googleDriveTest.root}
    >
      <ProtoAppHeader
        themeControl="light-dark"
        appTheme={theme}
        onAppTheme={(t) => {
          void setTheme(t)
        }}
        badge="drive-continuity"
        testId={testIds.googleDriveTest.appHeader}
        prototypeIndexTestId={testIds.googleDriveTest.backToPrototypeIndex}
      />

      <div className="flex h-9 shrink-0 items-center gap-2 border-b border-border px-3">
        <MaaterProtoSegmentTrack
          value={topView}
          onChange={(id) => setTopView(id as TopView)}
          options={[
            { id: 'prototype', label: 'Prototype' },
            { id: 'api', label: 'API Test' },
          ]}
        />
        <span className="text-[10px] text-muted-foreground">
          Continuity harness - chrome SoT = /?test=maater-ui
        </span>
      </div>

      <div className="flex min-h-0 flex-1">
        <ProtoIconRail active="documents" testId={testIds.googleDriveTest.iconRail} />
        <DriveModePanel
          surface={surface}
          onSurface={handleSurface}
          currentFolderId={currentFolderId}
          onSelectFolder={navigateFolder}
          connected={connected}
          authLoading={authLoading && authMock === 'live'}
          onConnect={() => void handleConnect()}
          onDisconnect={() => void disconnectProper()}
        />

        <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <div
            className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-1.5"
            data-testid={testIds.googleDriveTest.authMock}
          >
            <span className="text-[10px] font-medium text-muted-foreground">Auth mock</span>
            <MaaterProtoSegmentTrack
              value={authMock}
              onChange={(id) => setAuthMock(id as AuthMockFrame)}
              options={[
                { id: 'live', label: 'Live' },
                { id: 'disconnected', label: 'Not connected' },
                { id: 'blocked', label: 'Access blocked' },
              ]}
            />
          </div>
          <ProtoTabRow
            tabs={tabs}
            activeId={activeTabId}
            onSelect={(id) => handleSurface(id as Surface)}
            testId={testIds.googleDriveTest.tabRow}
          />
          {surface === 'drive' ? (
            <div className="flex items-center gap-1 border-b border-border px-2 py-1.5">
              <NavigationButtons
                canGoBack={folderHistory.index > 0}
                canGoForward={folderHistory.index < folderHistory.stack.length - 1}
                onGoBack={folderGoBack}
                onGoForward={folderGoForward}
                backTestId={testIds.googleDriveTest.folderBack}
                forwardTestId={testIds.googleDriveTest.folderForward}
              />
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0"
                disabled={currentFolderId === null}
                onClick={folderGoUp}
                title="Up one level"
                data-testid={testIds.googleDriveTest.folderUp}
              >
                <ArrowUp className="h-4 w-4" />
              </Button>
              <div className="min-w-0 flex-1 px-1">
                <AddressBar segments={breadcrumbSegments} className="text-xs" />
              </div>
              <Button
                size="sm"
                variant="outline"
                className="h-8 gap-1 text-xs"
                onClick={() => setShowPicker(true)}
              >
                <Import className="h-3.5 w-3.5" />
                Import
              </Button>
              <PreviewPanelToggle
                open={previewOpen}
                onToggle={() => setPreviewOpen((v) => !v)}
                testId={testIds.googleDriveTest.previewToggle}
              />
            </div>
          ) : (
            <ProtoTitleRow
              crumbs={titleRow.crumbs}
              onCrumb={(id) => {
                if (id === 'drive' || id === 'folder') handleSurface('drive')
                if (id === 'activity') handleSurface('activity')
                if (id === 'linked') handleSurface('linked')
              }}
              title={titleRow.title}
              subtitle={titleRow.subtitle}
              canGoBack={history.index > 0}
              canGoForward={history.index < history.stack.length - 1}
              onBack={goBack}
              onForward={goForward}
              testId={testIds.googleDriveTest.titleRow}
              backTestId={testIds.googleDriveTest.backButton}
              forwardTestId={testIds.googleDriveTest.forwardButton}
              actions={
                <>
                  {surface === 'viewer' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 gap-1 text-xs"
                      onClick={() => setShowPicker(true)}
                    >
                      <Import className="h-3.5 w-3.5" />
                      Import
                    </Button>
                  )}
                  <PreviewPanelToggle
                    open={previewOpen}
                    onToggle={() => setPreviewOpen((v) => !v)}
                    testId={testIds.googleDriveTest.previewToggle}
                  />
                </>
              }
            />
          )}

          {(surface === 'viewer' || surface === 'linked') && (
            <SyncActivityStrip
              frame={syncFrame}
              keepSynced={keepSynced}
              onSync={runSync}
              onToggleKeep={() => setKeepSynced((v) => !v)}
              onDemoFrame={setSyncFrame}
            />
          )}

          <PanelGroup direction="horizontal" className="min-h-0 flex-1">
            <Panel minSize={35} className="flex min-h-0 flex-col overflow-hidden">
            <div
              className="min-h-0 min-w-0 flex-1 overflow-y-auto"
              data-testid={testIds.googleDriveTest.mainList}
            >
              {(showBlockedEmpty || showDisconnectedEmpty) && (
                <div className="flex h-full min-h-[280px] flex-col items-center justify-center gap-3 p-6 text-center">
                  <GoogleDriveIcon className="h-10 w-10 text-muted-foreground" />
                  <h2 className="text-sm font-semibold">
                    {showBlockedEmpty
                      ? DRIVE_ACCESS_COPY.blockedTitle
                      : DRIVE_ACCESS_COPY.notConnectedTitle}
                  </h2>
                  <p className="max-w-sm text-xs text-muted-foreground">
                    {showBlockedEmpty
                      ? DRIVE_ACCESS_COPY.blockedBody
                      : DRIVE_ACCESS_COPY.notConnectedBody}
                  </p>
                  <div className="flex flex-col gap-2">
                    {showBlockedEmpty ? (
                      <>
                        <Button
                          size="sm"
                          onClick={() =>
                            showToast('Would POST /api/google-drive/access-request (operator inbox)')
                          }
                          data-testid={testIds.driveSidebar.requestAccess}
                        >
                          {DRIVE_ACCESS_COPY.requestAccessLabel}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setAuthMock('live')}>
                          {DRIVE_ACCESS_COPY.retryConnectLabel}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button size="sm" variant="outline" onClick={() => void handleConnect()}>
                          Connect Google Drive
                        </Button>
                        <button
                          type="button"
                          className="text-xs text-muted-foreground underline-offset-2 hover:underline"
                          onClick={() => setAuthMock('blocked')}
                        >
                          {DRIVE_ACCESS_COPY.needAccessLabel}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {!showBlockedEmpty && !showDisconnectedEmpty && surface === 'drive' && (
                <ul>
                  {listFiles.map((file) => {
                    const selected = selectedFileId === file.id
                    return (
                      <li key={file.id}>
                        <ExtensionListRow
                          selected={selected}
                          onSelect={() => selectFile(file.id)}
                          onOpen={() => {
                            if (file.type === 'folder') navigateFolder(file.id)
                            else openViewer(file.id)
                          }}
                          onContextMenu={(e) => {
                            selectFile(file.id)
                            setCtxMenu({
                              x: e.clientX,
                              y: e.clientY,
                              kind: 'file',
                              id: file.id,
                              fileId: file.id,
                            })
                          }}
                        >
                          <span className="mt-0.5">
                            <FileIcon type={file.type} />
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium">{file.name}</div>
                            <div className="mt-0.5 truncate text-xs text-muted-foreground">
                              {file.modified}
                            </div>
                          </div>
                          {file.type === 'shortcut' ? (
                            <span className="shrink-0 self-center text-xs text-muted-foreground">
                              Shortcut
                            </span>
                          ) : null}
                        </ExtensionListRow>
                      </li>
                    )
                  })}
                  {listFiles.length === 0 && (
                    <li className="px-6 py-8 text-center text-xs text-muted-foreground">
                      This folder is empty
                    </li>
                  )}
                </ul>
              )}

              {!showBlockedEmpty && !showDisconnectedEmpty && surface === 'activity' && (
                <ul>
                  {MOCK_ACTIVITY.map((row) => {
                    const selected = selectedActivityId === row.id
                    return (
                      <li key={row.id}>
                        <ExtensionListRow
                          selected={selected}
                          onSelect={() => {
                            setSelectedActivityId(row.id)
                            setSelectedFileId(row.fileId)
                          }}
                          onOpen={() => openViewer(row.fileId)}
                          onContextMenu={(e) => {
                            setSelectedActivityId(row.id)
                            setCtxMenu({
                              x: e.clientX,
                              y: e.clientY,
                              kind: 'activity',
                              id: row.id,
                              fileId: row.fileId,
                              linkedDocId: row.linkedDocId,
                            })
                          }}
                        >
                          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold">
                            {row.authorInitials}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium">{row.fileName}</div>
                            <div className="mt-0.5 truncate text-xs text-muted-foreground">
                              {row.author} · {row.timestamp}
                            </div>
                          </div>
                          <span className="shrink-0 self-center text-xs text-muted-foreground">
                            {row.kind}
                          </span>
                        </ExtensionListRow>
                      </li>
                    )
                  })}
                </ul>
              )}

              {!showBlockedEmpty && !showDisconnectedEmpty && surface === 'linked' && (
                <ul>
                  {MOCK_LINKED.map((doc) => {
                    const selected = selectedLinkedId === doc.id
                    return (
                      <li key={doc.id}>
                        <ExtensionListRow
                          selected={selected}
                          onSelect={() => {
                            setSelectedLinkedId(doc.id)
                            setSelectedFileId(doc.fileId)
                            setKeepSynced(doc.keepSynced)
                          }}
                          onOpen={() => openViewer(doc.fileId)}
                          onContextMenu={(e) => {
                            setSelectedLinkedId(doc.id)
                            setCtxMenu({
                              x: e.clientX,
                              y: e.clientY,
                              kind: 'linked',
                              id: doc.id,
                              fileId: doc.fileId,
                              keepSynced: doc.keepSynced,
                            })
                          }}
                        >
                          <FileText className="mt-0.5 h-4 w-4 shrink-0 text-blue-500/80" />
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium">{doc.title}</div>
                            <div className="mt-0.5 truncate text-xs text-muted-foreground">
                              {doc.displayPath}
                            </div>
                          </div>
                          {doc.keepSynced ? (
                            <span className="shrink-0 self-center text-xs text-primary">
                              Keep synced
                            </span>
                          ) : null}
                        </ExtensionListRow>
                      </li>
                    )
                  })}
                </ul>
              )}

              {surface === 'viewer' && viewerNode && (
                <div className="flex h-full min-h-[320px] flex-col p-4">
                  {syncFrame === 'conflict' ? (
                    <div
                      className="mb-3 rounded-lg border p-3 text-xs"
                      style={{ borderColor: 'var(--line)', background: 'var(--paper-deep)' }}
                    >
                      <p className="font-medium" style={{ color: 'var(--ink)' }}>
                        Conflict preview (mock DiffPreviewDialog)
                      </p>
                      <p className="mt-1" style={{ color: 'var(--ink-soft)' }}>
                        Accept Drive (upstream) or Keep Maater - Force push/pull stay in advanced
                        overflow.
                      </p>
                      <div className="mt-2 flex gap-2">
                        <Button size="sm" variant="outline" className="h-7 text-xs">
                          Accept Drive
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs">
                          Keep Maater
                        </Button>
                      </div>
                    </div>
                  ) : null}
                  {viewerMeta && viewerFileId ? (
                    <HarnessDrivePreviewStub
                      file={{
                        id: viewerFileId,
                        name: viewerNode.name,
                        type: viewerMeta.type,
                        mimeType: viewerMeta.mimeType,
                        shortcutTarget: viewerMeta.shortcutTarget,
                      }}
                      onImport={
                        viewerImportable
                          ? () => showToast('Import queued (mock)')
                          : undefined
                      }
                    />
                  ) : null}
                  <div className="mt-2 flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 gap-1 text-xs"
                      onClick={() => showToast('Open advanced: Force push / Force pull')}
                    >
                      <MoreHorizontal className="h-3.5 w-3.5" />
                      Advanced
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <ListSurfaceStatusBar
              tips={LIST_GESTURE_TIPS_DRIVE_WITH_PREVIEW}
              data-testid={testIds.googleDriveTest.statusBar}
              helpTestId={testIds.googleDriveTest.statusBarHelp}
              trailing={
                showBlockedEmpty || showDisconnectedEmpty ? null : surface === 'drive' ? (
                  <span className="text-[10px] text-muted-foreground/40 tabular-nums">
                    {listFiles.length} item{listFiles.length === 1 ? '' : 's'}
                  </span>
                ) : surface === 'activity' ? (
                  <span className="text-[10px] text-muted-foreground/40 tabular-nums">
                    {MOCK_ACTIVITY.length} event{MOCK_ACTIVITY.length === 1 ? '' : 's'}
                  </span>
                ) : surface === 'linked' ? (
                  <span className="text-[10px] text-muted-foreground/40 tabular-nums">
                    {MOCK_LINKED.length} doc{MOCK_LINKED.length === 1 ? '' : 's'}
                  </span>
                ) : null
              }
            />
            </Panel>

            {previewOpen && (
              <>
                <SecondaryPreviewResizeHandle />
                <SecondaryPanel
                  panelType="drive-preview"
                  panelId="drive-harness-preview"
                  defaultSize={PANEL_SIZES.SECONDARY_DEFAULT}
                  minSize={PANEL_SIZES.SECONDARY_MIN}
                  background="card"
                  className="min-w-0 overflow-hidden"
                >
                  <div
                    className="flex h-full min-h-0 flex-col"
                    data-testid={testIds.googleDriveTest.previewPane}
                  >
                    <PreviewPaneHeader
                      icon={
                        selectedDriveNode?.type === 'folder' ? (
                          <Folder className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        ) : (
                          <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        )
                      }
                      label={
                        selectedDriveNode?.type === 'folder'
                          ? 'Folder Preview'
                          : selectedDriveNode
                            ? 'File Preview'
                            : 'Preview'
                      }
                      chips={
                        selectedDriveNode ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded border border-border bg-muted text-muted-foreground font-semibold uppercase tracking-wider leading-none flex-shrink-0 truncate max-w-[140px]">
                            {selectedDriveNode.type === 'folder' ? 'Folder' : selectedDriveNode.type}
                          </span>
                        ) : undefined
                      }
                      onOpen={
                        selectedDriveNode
                          ? () => {
                              if (selectedDriveNode.type === 'folder') {
                                navigateFolder(selectedDriveNode.id)
                              } else {
                                openViewer(selectedDriveNode.id)
                              }
                            }
                          : undefined
                      }
                      openTitle={
                        selectedDriveNode?.type === 'folder' ? 'Open folder' : 'Open in tab'
                      }
                      onClose={() => setPreviewOpen(false)}
                    />
                    <div className="min-h-0 flex-1 overflow-auto">
                      {selectedDriveNode?.type === 'folder' ? (
                        <ul>
                          {(selectedDriveNode.children ?? []).length === 0 ? (
                            <li className="px-4 py-6 text-xs text-muted-foreground">
                              Empty folder
                            </li>
                          ) : (
                            (selectedDriveNode.children ?? []).map((child) => (
                              <li key={child.id}>
                                <ExtensionListRow
                                  selected={false}
                                  onSelect={() => selectFile(child.id)}
                                  onOpen={() => {
                                    if (child.type === 'folder') navigateFolder(child.id)
                                    else openViewer(child.id)
                                  }}
                                  onContextMenu={() => selectFile(child.id)}
                                  className="[&_[role=button]]:px-4 [&_[role=button]]:py-2.5"
                                >
                                  <span className="mt-0.5">
                                    <FileIcon type={child.type} />
                                  </span>
                                  <div className="min-w-0 flex-1">
                                    <div className="truncate text-sm font-medium">{child.name}</div>
                                    {child.modified ? (
                                      <div className="mt-0.5 truncate text-xs text-muted-foreground">
                                        {child.modified}
                                      </div>
                                    ) : null}
                                  </div>
                                </ExtensionListRow>
                              </li>
                            ))
                          )}
                        </ul>
                      ) : selectedMeta && selectedFileId ? (
                        <div className="p-3">
                          <HarnessDrivePreviewStub
                            compact
                            file={{
                              id: selectedFileId,
                              name:
                                findNode(MOCK_DRIVE, selectedFileId)?.name ??
                                MOCK_RECENT.find((r) => r.id === selectedFileId)?.name ??
                                'Selected file',
                              type: selectedMeta.type,
                              mimeType: selectedMeta.mimeType,
                              shortcutTarget: selectedMeta.shortcutTarget,
                            }}
                            onOpen={() => openViewer(selectedFileId)}
                            onImport={
                              selectedImportable
                                ? () => showToast('Import queued (mock)')
                                : undefined
                            }
                          />
                        </div>
                      ) : (
                        <p className="p-4 text-xs leading-relaxed text-muted-foreground">
                          Select a file or folder to preview, or choose Preview from the row menu.
                        </p>
                      )}
                    </div>
                  </div>
                </SecondaryPanel>
              </>
            )}
          </PanelGroup>
        </main>
      </div>

      {ctxMenu ? (
        <EntityContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          onClose={() => setCtxMenu(null)}
          entries={ctxMenuEntries}
          data-testid={testIds.googleDriveTest.contextMenu}
        />
      ) : null}

      {showPicker && (
        <ImportPicker
          onClose={() => setShowPicker(false)}
          onImport={(count) => {
            setShowPicker(false)
            showToast(`Imported ${count} file${count === 1 ? '' : 's'}`)
            handleSurface('linked')
          }}
        />
      )}

      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
    </div>
  )
}
